export type InputAction =
  | { type: 'move'; direction: 'left' | 'right' | 'none' }
  | { type: 'aim'; direction: 'up' | 'down' | 'none' }
  | { type: 'jump' }
  | { type: 'backflip' }
  | { type: 'chargeStart' }
  | { type: 'chargeRelease'; power: number }
  | { type: 'selectWeapon'; weaponId: string }
  | { type: 'setFuse'; timer: number }
  | { type: 'targetSelect'; x: number; y: number }
  | { type: 'parachute' }
  | { type: 'ropeExtend'; delta: number }
  | { type: 'ropeRelease' };

export interface InputFrame {
  playerId: string;
  teamId: string;
  tick: number;
  actions: InputAction[];
}

export interface InputSource {
  playerId: string;
  poll(): InputAction[];
}

export class InputManager {
  private sources: Map<string, InputSource> = new Map();
  private pendingFrames: InputFrame[] = [];
  private currentTick = 0;

  registerSource(source: InputSource): void {
    this.sources.set(source.playerId, source);
  }

  unregisterSource(playerId: string): void {
    this.sources.delete(playerId);
  }

  pollAll(activePlayerId: string, activeTeamId: string): InputFrame | null {
    const source = this.sources.get(activePlayerId);
    if (!source) return null;

    const actions = source.poll();
    if (actions.length === 0) return null;

    const frame: InputFrame = {
      playerId: activePlayerId,
      teamId: activeTeamId,
      tick: this.currentTick++,
      actions,
    };

    return frame;
  }

  queueRemoteFrame(frame: InputFrame): void {
    this.pendingFrames.push(frame);
    this.pendingFrames.sort((a, b) => a.tick - b.tick);
  }

  getNextFrame(): InputFrame | null {
    return this.pendingFrames.shift() || null;
  }

  hasPendingFrames(): boolean {
    return this.pendingFrames.length > 0;
  }

  clear(): void {
    this.pendingFrames = [];
    this.currentTick = 0;
  }
}

export class LocalKeyboardSource implements InputSource {
  playerId: string;

  private keyState: Set<string> = new Set();
  private pendingActions: InputAction[] = [];
  private chargePower = 0;
  private isCharging = false;
  private lastJumpTime = 0;
  private readonly DOUBLE_TAP_THRESHOLD = 250;

  constructor(playerId: string) {
    this.playerId = playerId;
    this.setupListeners();
  }

  private setupListeners(): void {
    window.addEventListener('keydown', (e) => this.handleKeyDown(e));
    window.addEventListener('keyup', (e) => this.handleKeyUp(e));
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (this.keyState.has(e.code)) return;
    this.keyState.add(e.code);

    switch (e.code) {
      case 'ArrowLeft':
      case 'KeyA':
        this.pendingActions.push({ type: 'move', direction: 'left' });
        break;
      case 'ArrowRight':
      case 'KeyD':
        this.pendingActions.push({ type: 'move', direction: 'right' });
        break;
      case 'ArrowUp':
      case 'KeyW':
        this.pendingActions.push({ type: 'aim', direction: 'up' });
        break;
      case 'ArrowDown':
      case 'KeyS':
        this.pendingActions.push({ type: 'aim', direction: 'down' });
        break;
      case 'Enter': {
        const now = Date.now();
        if (now - this.lastJumpTime < this.DOUBLE_TAP_THRESHOLD) {
          this.pendingActions.push({ type: 'backflip' });
          this.lastJumpTime = 0;
        } else {
          this.lastJumpTime = now;
          this.pendingActions.push({ type: 'jump' });
        }
        break;
      }
      case 'Space':
        if (!this.isCharging) {
          this.isCharging = true;
          this.chargePower = 0;
          this.pendingActions.push({ type: 'chargeStart' });
        }
        break;
      case 'Digit1':
        this.pendingActions.push({ type: 'selectWeapon', weaponId: 'grenade' });
        break;
      case 'Digit2':
        this.pendingActions.push({ type: 'selectWeapon', weaponId: 'bazooka' });
        break;
      case 'Digit3':
        this.pendingActions.push({ type: 'selectWeapon', weaponId: 'dynamite' });
        break;
      case 'Digit4':
        this.pendingActions.push({ type: 'selectWeapon', weaponId: 'cluster' });
        break;
      case 'Digit5':
        this.pendingActions.push({ type: 'selectWeapon', weaponId: 'shotgun' });
        break;
      case 'Digit6':
        this.pendingActions.push({ type: 'selectWeapon', weaponId: 'rope' });
        break;
      case 'Digit7':
        this.pendingActions.push({ type: 'selectWeapon', weaponId: 'airstrike' });
        break;
      case 'Digit8':
        this.pendingActions.push({ type: 'selectWeapon', weaponId: 'mine' });
        break;
      case 'Digit9':
        this.pendingActions.push({ type: 'selectWeapon', weaponId: 'teleport' });
        break;
      case 'Digit0':
        this.pendingActions.push({ type: 'selectWeapon', weaponId: 'parachute' });
        break;
      case 'KeyQ':
        this.pendingActions.push({ type: 'ropeExtend', delta: -10 });
        break;
      case 'KeyE':
        this.pendingActions.push({ type: 'ropeExtend', delta: 10 });
        break;
      case 'KeyR':
        this.pendingActions.push({ type: 'ropeRelease' });
        break;
    }
  }

  private handleKeyUp(e: KeyboardEvent): void {
    this.keyState.delete(e.code);

    switch (e.code) {
      case 'ArrowLeft':
      case 'KeyA':
      case 'ArrowRight':
      case 'KeyD':
        if (
          !this.keyState.has('ArrowLeft') &&
          !this.keyState.has('KeyA') &&
          !this.keyState.has('ArrowRight') &&
          !this.keyState.has('KeyD')
        ) {
          this.pendingActions.push({ type: 'move', direction: 'none' });
        }
        break;
      case 'ArrowUp':
      case 'KeyW':
      case 'ArrowDown':
      case 'KeyS':
        if (
          !this.keyState.has('ArrowUp') &&
          !this.keyState.has('KeyW') &&
          !this.keyState.has('ArrowDown') &&
          !this.keyState.has('KeyS')
        ) {
          this.pendingActions.push({ type: 'aim', direction: 'none' });
        }
        break;
      case 'Space':
        if (this.isCharging) {
          this.pendingActions.push({ type: 'chargeRelease', power: this.chargePower });
          this.isCharging = false;
          this.chargePower = 0;
        }
        break;
    }
  }

  updateCharge(deltaTime: number): void {
    if (this.isCharging) {
      this.chargePower = Math.min(100, this.chargePower + 100 * deltaTime);
    }
  }

  poll(): InputAction[] {
    const actions = [...this.pendingActions];
    this.pendingActions = [];
    return actions;
  }

  isKeyDown(code: string): boolean {
    return this.keyState.has(code);
  }

  destroy(): void {
    window.removeEventListener('keydown', (e) => this.handleKeyDown(e));
    window.removeEventListener('keyup', (e) => this.handleKeyUp(e));
  }
}
